import { AUTH_SERVICE, DATA_API_SERVICE, ENV } from "@/appConfig";
import { UserToken } from "@/api/tokens";
import router from "@/router";
import UserProfile, { UserProfileData } from "@/classes/userProfile";
import APIOrganization from "@/api/auth/organization";
import APIProperty from "@/api/auth/property";
import Role, { RoleEnum } from "@/classes/role";
import API, { AUTH } from "@/api/api";
import { AppAbilityService } from "@/appAbility";
import { UserStore } from "@/store";

function logout() {
  // Invalidate token in the backend
  // Fire and forget this request, no need to wait for it to logout
  AUTH.post(`${AUTH_SERVICE}/logout`, {
    // eslint-disable-next-line @typescript-eslint/camelcase
    access_token: UserToken.get(),
    // eslint-disable-next-line @typescript-eslint/camelcase
    refresh_token: UserToken.getRefresh()
  }).then(() => null);
  UserToken.remove();
  router.push({
    name: "sessionEnded"
  });
}

async function contactForAuthUser(user: UserProfile, description: string) {
  return API.post(`${DATA_API_SERVICE}/contact`, {
    subject: `Contact Request From Web User: ${user.email} (${user.name})}`,
    message: description
  });
}

async function login(email: string, password: string, realm?: string) {
  const loginPath = ENV === "gov" ? "/mfa/login" : "/login";
  const response = await AUTH.post(`${AUTH_SERVICE}${loginPath}`, {
    email,
    password,
    realm
  });
  if (response.data.access_token) {
    UserToken.update(response.data.access_token);
  }
  if (response.data.refresh_token) {
    UserToken.updateRefresh(response.data.refresh_token);
  }
  return response.data;
}

async function submitPhone(phone: string, token: string) {
  return AUTH.post(`${AUTH_SERVICE}/mfa/phone`, {
    phone,
    token
  });
}

async function submitCode(code: string, token: string) {
  const response = await AUTH.post(`${AUTH_SERVICE}/mfa/verify`, {
    code,
    token
  });
  if (response.data.access_token) {
    UserToken.update(response.data.access_token);
  }
  if (response.data.refresh_token) {
    UserToken.updateRefresh(response.data.refresh_token);
  }

  return response.data;
}

async function resendCode(token: string) {
  return AUTH.post(`${AUTH_SERVICE}/mfa/resend`, {
    token
  });
}

async function confirmUser(params: { password: string; token: string }, realm?: string) {
  const { password, token } = params;
  return API.post(
    `${DATA_API_SERVICE}/v2/user/confirm`,
    {
      password,
      token
    },
    ENV === "gov" ? { params: { realm } } : undefined
  );
}

async function changePassword(oldPassword: string, newPassword: string) {
  await API.post(`${DATA_API_SERVICE}/changePassword`, {
    oldPassword,
    newPassword
  });
  return true;
}

async function lostPassword(email: string, realm?: string) {
  await API.post(
    `${DATA_API_SERVICE}/v2/password/lost`,
    {
      email
    },
    ENV === "gov" ? { params: { realm } } : undefined
  );
  return true;
}

async function resetPassword(token: string, newPassword: string, realm?: string) {
  await API.post(
    `${DATA_API_SERVICE}/password/reset`,
    {
      token,
      newPassword
    },
    ENV === "gov" ? { params: { realm } } : undefined
  );
  return true;
}

async function refreshProfile() {
  AppAbilityService.update([]);
  UserStore.updateProfile(new UserProfile());
  const response = await API.get<UserProfileData>(`${DATA_API_SERVICE}/v2/user`);
  const roleName = response.data.role?.rName || "";
  const profile = new UserProfile({
    ...response.data,
    organization: response.data.organization,
    role: response.data.role ? Role.fromAPI(response.data.role) : null
  });
  UserStore.updateProfile(profile);
  AppAbilityService.update(profile.updateRules());

  // Update the role display and re-update the store
  let roleDisplay = "";
  if (roleName === RoleEnum.Admin || roleName === RoleEnum.AdminReadOnly || roleName === RoleEnum.Organization) {
    try {
      const organization = await APIOrganization.findById(response.data.organization);
      roleDisplay = organization.name;
    } catch (e) {
      // Only throw the error for non admins to prevent initial login
      if (roleName === RoleEnum.Organization) {
        throw e;
      }
    }
  } else if (roleName === RoleEnum.Property) {
    const property = await APIProperty.findPropertyById(response.data.property);
    roleDisplay = property.name;
  }
  profile.roleDisplay = roleDisplay;
  UserStore.updateProfile(profile);
  return profile;
}

const APIAuth = {
  confirmUser,
  contactForAuthUser,
  changePassword,
  refreshProfile,
  login,
  logout,
  lostPassword,
  resetPassword,
  submitPhone,
  submitCode,
  resendCode
};

export default APIAuth;
